import { stAnalytics } from "@repo/analytics";
import type { curator } from "@repo/client";
import { type Component, Show, createMemo, createSignal } from "solid-js";
import { BlockQuote } from "@core/components/BlockQuote";
import { Anchor } from "@core/components/cta/Anchor";
import { PrimaryCTA } from "@core/components/cta/PrimaryCTA";
import { SecondaryCTA } from "@core/components/cta/SecondaryCTA";
import { formatStoryTileRelevance } from "@core/lib/formatting/formatStoryTileRelevance";
import { getScreenType } from "@core/lib/ui/getScreenType";
import styles from "./StoryTileDrawerScreen.module.css";

export const StoryTileDrawerScreen: Component<{ tiles: curator.Citation[]; initial: string }> = (props) => {
  const tiles = createMemo(() => props.tiles);
  const active = () => tiles().find((a) => a.citationId === props.initial);
  const [current, setCurrent] = createSignal<curator.Citation | undefined>(active());
  const max = () => tiles().length - 1;

  const [position, setPosition] = createSignal<number>(
    tiles().findIndex((current) => current.citationId === props.initial),
  );

  const format = (text: string) => {
    const applyPrefix = /^[a-z]/.test(text);
    const applySuffix = !text.endsWith(".") && !text.endsWith("!");
    return `${applyPrefix ? "..." : ""} ${text}${applySuffix ? "..." : ""}`;
  };

  const next = (event: Event) => {
    event.preventDefault();
    if (position() !== max()) {
      setPosition(position() + 1);
      setCurrent(tiles()[position()]);
    }
    stAnalytics.track("click_tracking", {
      cta: "story_tile_navigate",
      position: "right_drawer",
      screen: getScreenType(),
    });
  };

  const previous = (event: Event) => {
    event.preventDefault();
    if (position() !== 0) {
      setPosition(position() - 1);
      setCurrent(tiles()[position()]);
    }
    stAnalytics.track("click_tracking", {
      cta: "story_tile_navigate",
      position: "right_drawer",
      screen: getScreenType(),
    });
  };

  return (
    <div class={styles["story-tile-drawer"]}>
      <Show when={tiles().length > 1}>
        <div class={styles["story-tile-drawer__top"]}>
          <div class={styles["story-tile-drawer__navigation"]}>
            <SecondaryCTA
              onClick={previous}
              label="Previous"
              accessiblePrefix="View "
              accessibleSuffix="citation."
              data-test-id="next-citation"
              inactive={position() === 0}
            />
            <PrimaryCTA
              onClick={next}
              label="Next"
              accessiblePrefix="View "
              accessibleSuffix="citation."
              data-test-id="next-citation"
              inactive={position() === max()}
            />
          </div>
          <p class={styles["story-tile-drawer__count-container"]}>
            <span class={styles["story-tile-drawer__title-small"]}>StoryTile&trade;</span>
            <span class={styles["story-tile-drawer__count"]}>{position() + 1}</span>
            <span class={styles["story-tile-drawer__count"]}>of</span>
            <span class={styles["story-tile-drawer__count"]}>{max() + 1}</span>
          </p>
        </div>
      </Show>
      <Show when={tiles().length === 1}>
        <p class={styles["story-tile-drawer__title"]}>StoryTile&trade;</p>
      </Show>

      <Show when={current() !== undefined}>
        <p class={styles["story-tile-drawer__subtitle"]}>Extracted from your assets</p>
        <BlockQuote class={styles["story-tile-drawer__extract"]}>
          <p>{format(current()?.text || "")}</p>
        </BlockQuote>
      </Show>
      <p class={styles["story-tile-drawer__docs"]}>
        <Anchor
          data-test-id="storytile-docs-link"
          accessibleSuffix="Visit our documentation to"
          label="Learn about the importance of StoryTiles™"
          href="https://docs.storytell.ai/under-the-hood/story-tiles"
          target="_blank"
        />
      </p>
    </div>
  );
};
